import _ from 'lodash';

/** Get a promise that resolves after given time */
export async function getTimer(timeout: number): Promise<void> {
  return new Promise<void>(resolve => {
    window.setTimeout(() => resolve(), timeout);
  });
}

/**
 * Make sure that async function completes no earlier than given delay.
 * Useful for avoiding "flashing" content when making short requests.
 */
export async function waitForTimeout<T>(
  callback: Promise<T>,
  timeout: number
): Promise<T> {
  const delayBy = getTimer(timeout);
  const response = await Promise.all([callback, delayBy]);
  return response[0];
}

export function rejectWithTimeout<T = unknown>(
  delay: number,
  rejectWith?: object | (() => any)
) {
  return new Promise<T>((_resolve, reject) => {
    setTimeout(() => {
      reject(_.isFunction(rejectWith) ? rejectWith() : rejectWith);
    }, delay);
  });
}

export function resolveWithTimeout<T = unknown>(
  delay: number,
  resolveWith?: object | (() => any)
) {
  return new Promise<T>(resolve => {
    setTimeout(() => {
      resolve(_.isFunction(resolveWith) ? resolveWith() : resolveWith);
    }, delay);
  });
}

export const delayBy = (options: {
  event?: string;
  response?: any;
  timeout?: number;
} = {}) => {
  const { response = {}, timeout = 1000 } = options;
  return (...args: []) => new Promise(resolve => setTimeout(() => {
    resolve(response);
    if (options.event || args.length) {
      console.debug(options.event || 'Args:', ...args);
    }
  }, timeout));
};

/**
 * Retry func with delay intervals until it returns true or maxTries number is hit.
 */
export const retry = async (func: () => boolean, maxTries = 3, delay = 200) => {
  let i = 0;
  while (i < maxTries) {
    await getTimer(delay);
    const res = func();
    if (!res) {
      i++;
    } else break;
  }
};

/**
 * Retry func with Promise until it resolves to true or maxTries number is hit.
 */
export const retryPromise = async (func: () => Promise<boolean | unknown>, maxTries = 3, currentTry = 1):
Promise<unknown> => {
  const i = currentTry;
  const res = await func();
  if (currentTry === maxTries) {
    return;
  }
  if (!res) {
    return retryPromise(func, maxTries, i + 1);
  } else return;
};
